// Audit log viewer page (ADM-012)
import { useState, useMemo } from 'react'
import {
  Typography,
  Breadcrumb,
  Table,
  Tag,
  Select,
  DatePicker,
  Button,
  Space,
  Card,
  Row,
  Col,
  Input,
  Spin,
} from 'antd'
import {
  DownloadOutlined,
  SearchOutlined,
  FileSearchOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'

import { useAuthStore } from '@/store/auth'
import { useAuditLog, useAuditLogFilters } from '@/hooks/useAuditLog'
import { exportAuditLogCsv } from '@/api/auditLog'
import type { AuditLogEntry, AuditLogParams } from '@/api/auditLog'

dayjs.locale('ru')

const { Title, Text } = Typography
const { RangePicker } = DatePicker

// ---- Russian labels ----

const ACTION_LABELS: Record<string, string> = {
  create: 'Создание',
  update: 'Изменение',
  delete: 'Удаление',
  deactivate: 'Деактивация',
  reactivate: 'Активация',
  revoke: 'Отзыв',
  login: 'Вход',
  logout: 'Выход',
  bulk_update: 'Массовое изменение',
  impersonate_start: 'Имперсонация (начало)',
  impersonate_stop: 'Имперсонация (конец)',
  deploy: 'Развёртывание',
}

const ENTITY_LABELS: Record<string, string> = {
  user: 'Сотрудник',
  organization: 'Организация',
  organization_capabilities: 'Возможности организации',
  access_group: 'Группа доступа',
  access_group_entry: 'Дашборд в группе',
  access_group_member: 'Участник группы',
  invite: 'Приглашение',
  rls_binding: 'RLS-привязка',
  filial: 'Филиал',
  department: 'Отделение',
  service: 'Услуга',
  bed_profile: 'Профиль коек',
  access_config: 'Конфигурация доступа',
  user_role_assignment: 'Платформенная роль',
  temporary_elevation: 'Временное повышение',
}

const ACTION_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  deactivate: 'orange',
  reactivate: 'cyan',
  revoke: 'volcano',
  login: 'default',
  logout: 'default',
  bulk_update: 'purple',
  impersonate_start: 'magenta',
  impersonate_stop: 'magenta',
  deploy: 'geekblue',
}

const ACTOR_TYPE_LABELS: Record<string, string> = {
  human: 'Человек',
  agent: 'Агент',
  service: 'Сервис',
}

const ACTOR_TYPE_COLORS: Record<string, string> = {
  human: 'blue',
  agent: 'purple',
  service: 'geekblue',
}

const ACTOR_SOURCE_LABELS: Record<string, string> = {
  user_session: 'UI/API пользователя',
  codex_cli: 'Codex CLI',
  codex: 'Codex',
}

// ---- Diff viewer component ----

function DiffView({
  oldValue,
  newValue,
}: {
  oldValue: Record<string, unknown> | null
  newValue: Record<string, unknown> | null
}) {
  if (!oldValue && !newValue) {
    return <Text type="secondary">Нет данных</Text>
  }

  const allKeys = Array.from(
    new Set([
      ...Object.keys(oldValue || {}),
      ...Object.keys(newValue || {}),
    ])
  )

  if (allKeys.length === 0) {
    return <Text type="secondary">Нет данных</Text>
  }

  return (
    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
          <th style={{ textAlign: 'left', padding: '4px 8px', width: '30%' }}>
            Поле
          </th>
          <th style={{ textAlign: 'left', padding: '4px 8px', width: '35%' }}>
            Было
          </th>
          <th style={{ textAlign: 'left', padding: '4px 8px', width: '35%' }}>
            Стало
          </th>
        </tr>
      </thead>
      <tbody>
        {allKeys.map((key) => {
          const ov = oldValue?.[key]
          const nv = newValue?.[key]
          const changed =
            JSON.stringify(ov) !== JSON.stringify(nv) && ov !== undefined && nv !== undefined

          return (
            <tr
              key={key}
              style={{
                borderBottom: '1px solid #f0f0f0',
                background: changed ? '#fff7e6' : undefined,
              }}
            >
              <td style={{ padding: '4px 8px', fontWeight: 500 }}>{key}</td>
              <td style={{ padding: '4px 8px' }}>
                {ov !== undefined ? (
                  <Text delete={changed} type={changed ? 'danger' : undefined}>
                    {formatValue(ov)}
                  </Text>
                ) : (
                  <Text type="secondary">&mdash;</Text>
                )}
              </td>
              <td style={{ padding: '4px 8px' }}>
                {nv !== undefined ? (
                  <Text type={changed ? 'success' : undefined}>
                    {formatValue(nv)}
                  </Text>
                ) : (
                  <Text type="secondary">&mdash;</Text>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '\u2014'
  if (typeof v === 'boolean') return v ? 'Да' : 'Нет'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function actorTypeLabel(actorType: string | null): string {
  return ACTOR_TYPE_LABELS[actorType || 'human'] || actorType || 'Человек'
}

function actorSourceLabel(actorSource: string | null): string | null {
  if (!actorSource) return null
  return ACTOR_SOURCE_LABELS[actorSource] || actorSource
}

function actorDisplayName(record: AuditLogEntry): string {
  if (record.actor_name) return record.actor_name
  if (record.actor_subject && record.actor_type !== 'human') return record.actor_subject
  if (record.actor_type === 'human' || !record.actor_type) return 'Пользовательский сеанс'
  return actorTypeLabel(record.actor_type)
}

function delegatedByLabel(record: AuditLogEntry): string | null {
  return record.delegated_by_name || record.delegated_by_email || null
}

// ---- Main page ----

export default function AuditLogPage() {
  const user = useAuthStore((s) => s.user)
  const isSuperadmin = user?.role === 'superadmin'

  // Filter state
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null)
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>()
  const [selectedAction, setSelectedAction] = useState<string | undefined>()
  const [selectedEntity, setSelectedEntity] = useState<string | undefined>()
  const [selectedActorType, setSelectedActorType] = useState<string | undefined>()
  const [selectedActorSource, setSelectedActorSource] = useState<string | undefined>()
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [exporting, setExporting] = useState(false)

  // Build query params
  const params: AuditLogParams = useMemo(
    () => ({
      date_from: dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
      date_to: dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
      user_id: selectedUserId || undefined,
      action: selectedAction || undefined,
      entity_type: selectedEntity || undefined,
      actor_type: selectedActorType || undefined,
      actor_source: selectedActorSource || undefined,
      search: searchText || undefined,
      page,
      page_size: pageSize,
    }),
    [
      dateRange,
      selectedUserId,
      selectedAction,
      selectedEntity,
      selectedActorType,
      selectedActorSource,
      searchText,
      page,
      pageSize,
    ]
  )

  const { data, isLoading } = useAuditLog(params)
  const { data: filters } = useAuditLogFilters()

  const handleExportCsv = async () => {
    setExporting(true)
    try {
      await exportAuditLogCsv({
        date_from: params.date_from,
        date_to: params.date_to,
        user_id: params.user_id,
        action: params.action,
        entity_type: params.entity_type,
        actor_type: params.actor_type,
        actor_source: params.actor_source,
      })
    } finally {
      setExporting(false)
    }
  }

  const handleResetFilters = () => {
    setDateRange(null)
    setSelectedUserId(undefined)
    setSelectedAction(undefined)
    setSelectedEntity(undefined)
    setSelectedActorType(undefined)
    setSelectedActorSource(undefined)
    setSearchText('')
    setPage(1)
  }

  // Table columns
  const columns: ColumnsType<AuditLogEntry> = [
    {
      title: 'Дата и время',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
      render: (dt: string) =>
        dt ? dayjs(dt).format('DD.MM.YYYY HH:mm:ss') : '\u2014',
    },
    {
      title: 'Аккаунт',
      key: 'user',
      width: 200,
      ellipsis: true,
      render: (_: unknown, record: AuditLogEntry) =>
        record.user_name || record.user_email || '\u2014',
    },
    {
      title: 'Исполнитель',
      key: 'actor',
      width: 230,
      ellipsis: true,
      render: (_: unknown, record: AuditLogEntry) => {
        const actorType = record.actor_type || 'human'
        const source = actorSourceLabel(record.actor_source)
        const delegatedBy = delegatedByLabel(record)

        return (
          <Space direction="vertical" size={0}>
            <Space size={6}>
              <Tag color={ACTOR_TYPE_COLORS[actorType] || 'default'}>
                {actorTypeLabel(actorType)}
              </Tag>
              <Text>{actorDisplayName(record)}</Text>
            </Space>
            {(source || delegatedBy) && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[source, delegatedBy ? `от ${delegatedBy}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
            )}
          </Space>
        )
      },
    },
    {
      title: 'Действие',
      dataIndex: 'action',
      key: 'action',
      width: 160,
      render: (action: string) => (
        <Tag color={ACTION_COLORS[action] || 'default'}>
          {ACTION_LABELS[action] || action}
        </Tag>
      ),
    },
    {
      title: 'Объект',
      dataIndex: 'entity_type',
      key: 'entity_type',
      width: 180,
      render: (et: string) => ENTITY_LABELS[et] || et,
    },
    {
      title: 'ID объекта',
      dataIndex: 'entity_id',
      key: 'entity_id',
      width: 120,
      ellipsis: true,
      render: (id: string | null) => id || '\u2014',
    },
    ...(isSuperadmin
      ? [
          {
            title: 'Организация',
            dataIndex: 'org_name',
            key: 'org_name',
            width: 160,
            ellipsis: true,
            render: (name: string | null) => name || '\u2014',
          } as const,
          {
            title: 'IP',
            dataIndex: 'ip_address',
            key: 'ip_address',
            width: 130,
            render: (ip: string | null) => ip || '\u2014',
          } as const,
        ]
      : []),
  ]

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Журнал действий' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          <FileSearchOutlined style={{ marginRight: 8 }} />
          Журнал действий
        </Title>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportCsv}
          loading={exporting}
        >
          Экспорт CSV
        </Button>
      </Space>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              value={dateRange}
              onChange={(val) => {
                setDateRange(val)
                setPage(1)
              }}
              style={{ width: '100%' }}
              placeholder={['Дата от', 'Дата до']}
              format="DD.MM.YYYY"
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Аккаунт"
              allowClear
              showSearch
              optionFilterProp="label"
              value={selectedUserId}
              onChange={(val) => {
                setSelectedUserId(val)
                setPage(1)
              }}
              style={{ width: '100%' }}
              options={(filters?.users || []).map((u) => ({
                value: u.id,
                label: u.full_name || u.email,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Исполнитель"
              allowClear
              value={selectedActorType}
              onChange={(val) => {
                setSelectedActorType(val)
                setPage(1)
              }}
              style={{ width: '100%' }}
              options={(filters?.actor_types || []).map((type) => ({
                value: type,
                label: actorTypeLabel(type),
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Источник"
              allowClear
              value={selectedActorSource}
              onChange={(val) => {
                setSelectedActorSource(val)
                setPage(1)
              }}
              style={{ width: '100%' }}
              options={(filters?.actor_sources || []).map((source) => ({
                value: source,
                label: actorSourceLabel(source) || source,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Действие"
              allowClear
              value={selectedAction}
              onChange={(val) => {
                setSelectedAction(val)
                setPage(1)
              }}
              style={{ width: '100%' }}
              options={(filters?.actions || []).map((a) => ({
                value: a,
                label: ACTION_LABELS[a] || a,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Тип объекта"
              allowClear
              value={selectedEntity}
              onChange={(val) => {
                setSelectedEntity(val)
                setPage(1)
              }}
              style={{ width: '100%' }}
              options={(filters?.entity_types || []).map((e) => ({
                value: e,
                label: ENTITY_LABELS[e] || e,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Input
              placeholder="Поиск по ID"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value)
                setPage(1)
              }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={2}>
            <Button onClick={handleResetFilters} block>
              Сбросить
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      {isLoading && !data ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          size="small"
          loading={isLoading}
          locale={{ emptyText: 'Нет записей в журнале' }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} из ${total}`,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '8px 0' }}>
                <DiffView
                  oldValue={record.old_value}
                  newValue={record.new_value}
                />
                {(record.actor_subject || record.delegated_by_email) && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Исполнитель: {actorDisplayName(record)}
                      {record.actor_subject ? ` · subject: ${record.actor_subject}` : ''}
                      {delegatedByLabel(record)
                        ? ` · делегировал: ${delegatedByLabel(record)}`
                        : ''}
                    </Text>
                  </div>
                )}
                {isSuperadmin && record.user_agent && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      User-Agent: {record.user_agent}
                    </Text>
                  </div>
                )}
              </div>
            ),
            rowExpandable: (record) =>
              !!(
                record.old_value ||
                record.new_value ||
                record.user_agent ||
                record.actor_subject ||
                record.delegated_by_email
              ),
          }}
        />
      )}
    </div>
  )
}
